/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import LinkItem from '@atlaskit/menu/link-item';

import type { AnchorNavItem, PageNavItem } from './navigationItems';
import { anchorHref } from './navigationItems';
import { useActiveAnchorToken } from './useActiveAnchorToken';
import { useAnchorItemClick } from './useAnchorItemClick';
import { usePageItemClick } from './usePageItemClick';

type AnchorLinkItemProps = AnchorNavItem & {
	testId?: string;
};

/**
 * One anchor entry as a menu `LinkItem`, shared by `NavItemAnchorMenu` and
 * `NavMenu`'s "On this page" section so both popouts render the exact same
 * item for a given token: same href, same label, same click behaviour, and
 * the same selected state, which marks whichever item the URL fragment names
 * so the open menu agrees with the trigger that summarises it.
 */
export const AnchorLinkItem = ({
	token,
	message,
	testId = `nav-anchor-item-${token}`,
}: AnchorLinkItemProps): JSX.Element => {
	const onClick = useAnchorItemClick(token);
	const isSelected = useActiveAnchorToken() === token;

	return (
		<LinkItem href={anchorHref(token)} onClick={onClick} isSelected={isSelected} testId={testId}>
			<FormattedMessage {...message} />
		</LinkItem>
	);
};

type PageLinkItemProps = PageNavItem & {
	testId?: string;
};

/**
 * One page link as a menu `LinkItem`, used by the hamburger's "Pages"
 * section. The inline page links in `NavButtons` render their own plain `<a>`
 * for the underline treatment, but share this same click behaviour via
 * `usePageItemClick`.
 */
export const PageLinkItem = ({
	path,
	routeName,
	message,
	testId = `nav-page-item-${routeName}`,
}: PageLinkItemProps): JSX.Element => {
	const { isActive, onClick } = usePageItemClick(path, routeName);

	return (
		<LinkItem href={path} onClick={onClick} isSelected={isActive} testId={testId}>
			<FormattedMessage {...message} />
		</LinkItem>
	);
};
